import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SHRTLogo } from '@/components/SHRTLogo';
import { 
  ArrowLeft, 
  Monitor, 
  Smartphone, 
  Apple,
  Chrome,
  Share,
  PlusSquare,
  MoreVertical,
  Download,
  Menu,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

const InstallGuidePage = () => {
  const [activeTab, setActiveTab] = useState(detectPlatform);
  const detected = detectPlatform();
  const [expandedStep, setExpandedStep] = useState(null);

  const toggleStep = (step) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <SHRTLogo size="sm" />
            <div>
              <p className="font-['Oswald'] text-sm font-bold tracking-wider">S.H.R.T.</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Mitgliederportal</p>
            </div>
          </Link>
          <Link 
            to="/login" 
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Zum Login
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="font-['Oswald'] text-2xl sm:text-3xl font-bold uppercase tracking-wider mb-3">
            App installieren
          </h1>
          <p className="text-muted-foreground">
            Installieren Sie das S.H.R.T. Portal als App auf Ihrem Gerät für schnellen Zugriff.
          </p>
        </div>

        {/* Detected Device Hint */}
        <div className="text-center mb-4">
          <p className="text-xs text-muted-foreground">
            {detected === 'ios' && 'iPhone / iPad erkannt'}
            {detected === 'android' && 'Android-Gerät erkannt'}
            {detected === 'desktop' && 'Desktop-Browser erkannt'}
            {' — '}Anleitung wird automatisch angezeigt
          </p>
        </div>

        {/* Platform Tabs */}
        <div className="flex justify-center gap-2 mb-8">
          <button
            onClick={() => setActiveTab('ios')}
            className={`flex items-center gap-2 px-4 py-3 border-2 transition-all ${
              activeTab === 'ios' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:border-primary/50'
            }`}
          >
            <Apple className="w-5 h-5" />
            <span className="font-medium">iPhone / iPad</span>
          </button>
          <button
            onClick={() => setActiveTab('android')}
            className={`flex items-center gap-2 px-4 py-3 border-2 transition-all ${
              activeTab === 'android' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:border-primary/50'
            }`}
          >
            <Smartphone className="w-5 h-5" />
            <span className="font-medium">Android</span>
          </button>
          <button
            onClick={() => setActiveTab('desktop')}
            className={`flex items-center gap-2 px-4 py-3 border-2 transition-all ${
              activeTab === 'desktop' 
                ? 'border-primary bg-primary/10 text-primary' 
                : 'border-border hover:border-primary/50'
            }`}
          >
            <Monitor className="w-5 h-5" />
            <span className="font-medium">Desktop</span>
          </button>
        </div>

        {/* iOS Instructions */}
        {activeTab === 'ios' && (
          <div className="space-y-4">
            <div className="bg-card border border-border p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                <strong>Voraussetzung:</strong> Safari Browser (andere Browser wie Chrome unterstützen keine PWA-Installation auf iOS)
              </p>
            </div>

            {/* Step 1 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Website in Safari öffnen</h3>
                  <p className="text-sm text-muted-foreground">
                    Öffnen Sie <strong>member.shrt.ch</strong> im Safari Browser
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Teilen-Button antippen</h3>
                  <p className="text-sm text-muted-foreground">
                    Tippen Sie auf das Teilen-Symbol <Share className="inline w-4 h-4 mx-1" /> unten in der Mitte der Safari-Leiste
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">"Zum Home-Bildschirm" wählen</h3>
                  <p className="text-sm text-muted-foreground">
                    Scrollen Sie im Menü nach unten und tippen Sie auf <strong>"Zum Home-Bildschirm"</strong> <PlusSquare className="inline w-4 h-4 mx-1" />
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">"Hinzufügen" bestätigen</h3>
                  <p className="text-sm text-muted-foreground">
                    Tippen Sie oben rechts auf <strong>"Hinzufügen"</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Success */}
            <div className="bg-green-500/10 border border-green-500/30 p-4 flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-green-600">Fertig!</p>
                <p className="text-sm text-muted-foreground">
                  Die S.H.R.T. App erscheint jetzt auf Ihrem Home-Bildschirm und kann wie eine normale App geöffnet werden.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Android Instructions */}
        {activeTab === 'android' && (
          <div className="space-y-4">
            <div className="bg-card border border-border p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                <strong>Empfohlen:</strong> Chrome Browser (funktioniert auch mit anderen Browsern)
              </p>
            </div>

            {/* Step 1 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Website in Chrome öffnen</h3>
                  <p className="text-sm text-muted-foreground">
                    Öffnen Sie <strong>member.shrt.ch</strong> im Chrome Browser
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Menü öffnen</h3>
                  <p className="text-sm text-muted-foreground">
                    Tippen Sie auf die drei Punkte <MoreVertical className="inline w-4 h-4 mx-1" /> oben rechts
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">"App installieren" oder "Zum Startbildschirm hinzufügen"</h3>
                  <p className="text-sm text-muted-foreground">
                    Wählen Sie <strong>"App installieren"</strong> oder <strong>"Zum Startbildschirm hinzufügen"</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Installation bestätigen</h3>
                  <p className="text-sm text-muted-foreground">
                    Tippen Sie auf <strong>"Installieren"</strong> im erscheinenden Dialog
                  </p>
                </div>
              </div>
            </div>

            {/* Success */}
            <div className="bg-green-500/10 border border-green-500/30 p-4 flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-green-600">Fertig!</p>
                <p className="text-sm text-muted-foreground">
                  Die S.H.R.T. App ist jetzt installiert und erscheint in Ihrer App-Übersicht.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Instructions */}
        {activeTab === 'desktop' && (
          <div className="space-y-4">
            <div className="bg-card border border-border p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                <strong>Unterstützte Browser:</strong> Chrome, Edge, Brave (Firefox unterstützt keine PWA-Installation)
              </p>
            </div>

            {/* Step 1 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Website öffnen</h3>
                  <p className="text-sm text-muted-foreground">
                    Öffnen Sie <strong>member.shrt.ch</strong> in Chrome, Edge oder Brave
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">Installations-Icon suchen</h3>
                  <p className="text-sm text-muted-foreground">
                    In der Adressleiste erscheint rechts ein <strong>Installations-Symbol</strong> <Download className="inline w-4 h-4 mx-1" /> oder <PlusSquare className="inline w-4 h-4 mx-1" />
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-card border border-border">
              <div className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-bold">App installieren</h3>
                  <p className="text-sm text-muted-foreground">
                    Klicken Sie auf das Symbol und bestätigen Sie mit <strong>"Installieren"</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Alternative */}
            <div className="bg-secondary/30 border border-border p-4">
              <p className="text-sm font-medium mb-2">Alternative über das Menü:</p>
              <p className="text-sm text-muted-foreground">
                Klicken Sie auf die drei Punkte <MoreVertical className="inline w-4 h-4 mx-1" /> → <strong>"S.H.R.T. installieren..."</strong> oder <strong>"Als App installieren"</strong>
              </p>
            </div>

            {/* Success */}
            <div className="bg-green-500/10 border border-green-500/30 p-4 flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-green-600">Fertig!</p>
                <p className="text-sm text-muted-foreground">
                  Die App öffnet sich in einem eigenen Fenster und ist über Ihre Taskleiste/Dock zugänglich.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-10 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Haben Sie bereits einen Account?
          </p>
          <Link 
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors"
          >
            Jetzt einloggen
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <div className="flex justify-center gap-4 mb-2">
            <Link to="/agb" className="hover:text-foreground">AGB</Link>
            <span>|</span>
            <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
          </div>
          © {new Date().getFullYear()} Swiss Historic Racing Team
        </div>
      </footer>
    </div>
  );
};

export default InstallGuidePage;
